import { Button, Popconfirm, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader } from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { areaLib } from "@/utils/areaLib";
import DetailJson, { type DetailJsonBlock } from "@/views/components/detailJson";
import { useUserStore } from "@/views/stores";
import { ENTERPRISE_SHOW_LOCATION, ENTERPRISE_SUPPLIER_TYPE } from "@zhihaoscm/meta";
import type { SupplierRow } from "./types";

export interface SupplierFile {
    id?: number | string;
    path?: string;
}

export interface SupplierDetailPageProps {
    deleteSupplier?: (id: number | string) => Promise<void>;
    loadSupplier?: (id: number | string) => Promise<SupplierRow>;
    loadSupplierFiles?: (ids: number[]) => Promise<SupplierFile[]>;
    permissions?: string[];
}

/** 复用 Vue 供应商详情接口。 */
export async function loadSupplierDetail(id: number | string): Promise<SupplierRow> {
    const response = await request.service.get<SupplierRow>(`/admin/supplier-admin/find/${id}`);
    return response.data;
}

/** 复用 Vue 供应商删除接口，详情页只在具备处理权限时暴露入口。 */
export async function deleteSupplierDetail(id: number | string): Promise<void> {
    await request.service.delete(`/admin/supplier-admin/delete/${id}`);
}

/** 复用 Vue 文件批量接口，将文件 ID 转换为详情页可展示的媒体地址。 */
export async function loadSupplierFiles(ids: number[]): Promise<SupplierFile[]> {
    const response = await request.service.post<SupplierFile[]>("/dataService/admin/file/ids", ids);
    return response.data;
}

/** 将 Vue 供应商详情返回的类型和展示位置枚举统一为数字。 */
function normalizeSupplierDetail(result: SupplierRow): SupplierRow {
    return {
        ...result,
        supplier: result.supplier
            ? {
                  ...result.supplier,
                  type: result.supplier.type == null ? undefined : Number(result.supplier.type),
                  showLocation: result.supplier.showLocation == null ? undefined : Number(result.supplier.showLocation)
              }
            : undefined
    };
}

/**
 * DetailHeader 会把空字符串归一为 "--"；空节点可保留旧 Vue 空编码，同时不影响其他详情页。
 */
function preserveSupplierCode(value?: string) {
    return value === "" ? <span aria-label="空供应商编码" /> : (value ?? "-");
}

/** 供应商详情页，展示 Vue 详情页中的核心企业字段。 */
export function SupplierDetailPage({
    deleteSupplier: remove = deleteSupplierDetail,
    loadSupplier = loadSupplierDetail,
    loadSupplierFiles: loadFiles = loadSupplierFiles,
    permissions
}: SupplierDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions || storePermissions;
    const canWrite = currentPermissions.includes("ROLE_SUPPLIER_DEAL");
    const id = new URLSearchParams(location.search).get("id");
    const [supplier, setSupplier] = useState<SupplierRow["supplier"]>();
    const [hasConsignment, setHasConsignment] = useState(false);
    const [supplierFiles, setSupplierFiles] = useState<Record<string, string>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        void loadSupplier(id)
            .then(result => {
                const normalizedResult = normalizeSupplierDetail(result);
                setSupplier(normalizedResult.supplier);
                setHasConsignment(Boolean(normalizedResult.productConsignment?.length));
                const fileIds = [
                    normalizedResult.supplier?.logoFileId,
                    normalizedResult.supplier?.businessFileId,
                    normalizedResult.supplier?.mobileSideFileId,
                    normalizedResult.supplier?.pcSideFileId
                ]
                    .filter(value => value != null)
                    .map(Number)
                    .filter(value => Number.isFinite(value));
                if (fileIds.length) {
                    void loadFiles(fileIds)
                        .then(files => {
                            setSupplierFiles(
                                files.reduce<Record<string, string>>((fileMap, file) => {
                                    if (file.id != null && file.path) fileMap[String(file.id)] = file.path;
                                    return fileMap;
                                }, {})
                            );
                        })
                        .catch(() => setSupplierFiles({}));
                }
            })
            .catch(() => setErrorMessage("供应商详情加载失败"));
    }, [id, loadFiles, loadSupplier]);

    const deleteCurrentSupplier = async () => {
        if (!id) return;
        try {
            // 删除失败时保留当前详情页，避免错误冒泡或误跳转到列表。
            await remove(id);
            void navigate("/supplierManagement/enterpriseManage");
        } catch {
            setErrorMessage("供应商删除失败");
        }
    };

    // 旧 Vue MediaWrap 缺图时只显示固定尺寸占位，不向页面暴露内部文件 ID。
    const renderSupplierFile = (id: number | string | undefined, alt: string) => {
        const path = id == null ? undefined : supplierFiles[String(id)];
        return path ? (
            <img alt={alt} className="supplier-detail__media-image" src={path} />
        ) : (
            <div
                aria-label={`${alt}暂无图片`}
                className="supplier-detail__media-placeholder"
                style={{ height: "135px", width: "240px" }}
            >
                <div className="supplier-detail__media-placeholder-content">
                    <i aria-hidden="true" className="iconfont icon-picture" />
                    <span className="supplier-detail__media-placeholder-tip">暂无图片</span>
                </div>
            </div>
        );
    };

    /** 旧 Vue MediaWrap 在 rowSlot 内使用纵向 BaseItem 呈现媒体标签与预览。 */
    const renderSupplierMedia = (label: string, id: number | string | undefined, alt: string) => (
        <ZhBaseItem colon={false} infoDirection="vertical" label={label}>
            {renderSupplierFile(id, alt)}
        </ZhBaseItem>
    );

    // 字段顺序、跨列和分块直接复刻旧 Vue enterpriseManage/detail.vue 的 columns。
    const detailColumns: DetailJsonBlock[] = [
        {
            sectionTitle: "基本信息",
            list: [
                { label: "集团", value: supplier?.groupCompanyName || "--" },
                { label: "供应商名称", value: supplier?.supplierName || "--" },
                {
                    label: "类型",
                    value: ENTERPRISE_SUPPLIER_TYPE.getIntroByCode(supplier?.type) || "--"
                },
                {
                    label: "所在区域",
                    value:
                        `${areaLib.getFullAreaName(
                            supplier?.provinceCode,
                            supplier?.cityCode,
                            supplier?.regionCode
                        )}${supplier?.address || ""}` || "--"
                },
                {
                    label: "展示位置",
                    value: ENTERPRISE_SHOW_LOCATION.getIntroByCode(supplier?.showLocation) || "--"
                },
                { label: "企业标签", value: supplier?.labels?.length ? supplier.labels.join(";") : "--" },
                { label: "联系人", value: supplier?.contactPerson || "--" },
                { label: "联系电话", value: supplier?.contactPhone || "--" }
            ]
        },
        {
            sectionTitle: "供应商信息",
            list: [
                { label: "主营品类", value: supplier?.mainCategories || "--", span: 4 },
                { label: "统一社会信用代码", value: supplier?.socialCreditCode || "--" },
                { label: "法人姓名", value: supplier?.legalRepresentative || "--", span: 3 },
                { rowSlot: "logoFile" },
                { rowSlot: "businessFile" },
                { rowSlot: "mobileSideFile" },
                { rowSlot: "pcSideFile" },
                { label: "企业介绍", value: supplier?.description || "--", span: 4 }
            ]
        }
    ];

    return (
        <section aria-label="supplier-detail-page" className="detail-page flex min-h-full flex-col">
            <div className="section-card mb-2">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: preserveSupplierCode(supplier?.code) }]}
                    rightInfos={[
                        { label: "创建时间", value: dateFormat(supplier?.createdTime, DATETIME_PICKER_FORMAT) }
                    ]}
                    title="供应商详情"
                />
            </div>
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            <DetailJson
                columns={detailColumns}
                slots={{
                    logoFile: () => renderSupplierMedia("企业logo", supplier?.logoFileId, "企业 Logo"),
                    businessFile: () => renderSupplierMedia("营业执照", supplier?.businessFileId, "营业执照"),
                    mobileSideFile: () =>
                        renderSupplierMedia("供应商首页广告-移动端", supplier?.mobileSideFileId, "移动端首页广告"),
                    pcSideFile: () => renderSupplierMedia("供应商首页广告-PC端", supplier?.pcSideFileId, "PC端首页广告")
                }}
            />
            <Space className="operation-fixed-btn" size={0}>
                {canWrite ? (
                    <Button
                        aria-label="编辑供应商"
                        onClick={() => void navigate(`/supplierManagement/enterpriseManage/edit?id=${id || ""}`)}
                    >
                        修改
                    </Button>
                ) : null}
                {canWrite && !hasConsignment ? (
                    <Popconfirm
                        cancelText="取消"
                        okText="确定"
                        title="确认删除该供应商吗？"
                        onConfirm={() => void deleteCurrentSupplier()}
                    >
                        <Button aria-label="删除供应商" danger>
                            删除
                        </Button>
                    </Popconfirm>
                ) : null}
                <Button onClick={() => void navigate("/supplierManagement/enterpriseManage")}>返回</Button>
            </Space>
        </section>
    );
}

export default SupplierDetailPage;
